import {createContext, useState, useEffect, useRef} from "react";
import {useNavigate} from "react-router-dom";

export const AppStateContext = createContext();

//const API_URL = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_API_URL_PROD : process.env.REACT_APP_API_URL;

const API_URL = (() => {
  if (typeof window === 'undefined') return '';

  const host = window.location.hostname;
  const protocol = window.location.protocol;

  if (host === 'localhost') {
    return `http://localhost:4000`; // local dev backend
  }

  // test/prod na tym samym hostingu
  return `${protocol}//${host}`;
})();

export const AppStateProvider = ({ socket, children }) => {

    const [currentQuest, setCurrentQuest] = useState(null);
    const [teamToken, setTeamToken] = useState(null);
    const [teamData, setTeamData] = useState(null);
    const [chatNotification, setChatNotification] = useState(null);
    const [questNotification, setQuestNotification] = useState(null);
    const [gameFinished, setGameFinished] = useState(false);

    const [chatMessagesRead, setChatMessagesRead] = useState(false);

    const currentQuestRef = useRef(currentQuest);

    const reconnectAttempts = useRef(0);
    const maxReconnectAttempts = 5;

    const navigate = useNavigate();

    const correctAnswerAudioRef = useRef(null);
    const takenHintAudioRef = useRef(null);
    const wrongAnswerAudioRef = useRef(null);

    useEffect(() => {
        const handleConnect = () => {
            console.log('WebSocket connected, teamtoken:', teamToken);
            reconnectAttempts.current = 0;

            // Dołącz do pokoju zespołu po połączeniu
            if (teamToken) {
                console.log('Joining team room:', teamToken);
                socket.emit("joinTeamRoom", { teamToken });
                
                // Odśwież dane po połączeniu
                getTeamData(teamToken);
            }
        };

        const handleDisconnect = (reason) => {
            console.log('WebSocket disconnected:', reason);
            
            // Automatyczne ponowne łączenie
            if (reconnectAttempts.current < maxReconnectAttempts) {
                reconnectAttempts.current++;
                console.log(`Próba ponownego połączenia ${reconnectAttempts.current}/${maxReconnectAttempts}...`);
                
                setTimeout(() => {
                    if (!socket.connected) {
                        socket.connect();
                    }
                }, 2000 * reconnectAttempts.current); // zwiększające się opóźnienie
            }
        };

        const handleConnectError = (error) => {
            console.error('Connection error:', error);
        };


        //user wrócił do aplikacji po minimalizacji lub przejściu w tle
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                console.log('Użytkownik wrócił do aplikacji');
                
                // Sprawdź połączenie socket
                if (!socket.connected) {
                    console.log('Socket rozłączony, próba ponownego połączenia...');
                    socket.connect();
                } else {
                    // Jeśli socket jest połączony, dołącz ponownie do pokoju
                    if (teamToken) {
                        console.log('Rejoining team room:', teamToken);
                        socket.emit("joinTeamRoom", { teamToken });
                    }
                }
                
                // Odśwież dane drużyny; drugi call po 2s na wypadek gdy sieć
                // nie jest jeszcze gotowa zaraz po powrocie z tła
                if (teamToken) {
                    //getTeamData(teamToken);
                    setTimeout(() => getTeamData(teamToken), 2000);
                }
            } else {
                console.log('Aplikacja zminimalizowana lub w tle');
            }
        };

        // Jeśli socket jest już połączony przy inicjalizacji
        if (socket.connected && teamToken) {
            console.log('Socket already connected, joining room:', teamToken);
            socket.emit("joinTeamRoom", { teamToken });
        }

        socket.on('connect', handleConnect);
        socket.on('disconnect', handleDisconnect);
        socket.on('connect_error', handleConnectError);


        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            socket.off('connect', handleConnect);
            socket.off('disconnect', handleDisconnect);
            socket.off('connect_error', handleConnectError);

            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [socket, teamToken]);

    function playAnswerOkAudio() {
        if (correctAnswerAudioRef.current) {
            correctAnswerAudioRef.current.currentTime = 0;
            correctAnswerAudioRef.current.play().catch((err) => {
                console.error('Audio play error:', err);
            });
        }
    }

    function playTakenHintAudio() {
        if (takenHintAudioRef.current) {
            takenHintAudioRef.current.currentTime = 0;
            takenHintAudioRef.current.play().catch((err) => {
                console.error('Audio play error:', err);
            });
        }
    }

    function playWrongAnswerAudio() {
        if (wrongAnswerAudioRef.current) {
            wrongAnswerAudioRef.current.currentTime = 0;
            wrongAnswerAudioRef.current.play().catch((err) => {
                console.error('Audio play error:', err);
            });
        }
    }

    useEffect(() => {
        currentQuestRef.current = currentQuest;
    }, [currentQuest]);

    useEffect(() => {
        function handleMessage(event) {
            if(!teamToken) return;

            if(!currentQuestRef) return;
            const currentQuestData = currentQuestRef.current;

            // window.parent.postMessage(
            //     {
            //       type: 'AR_EVENT',
            //       quest_id: 'quest-8',
            //         status: 'success'
            //     },
            //     '*'
            //   )

            if (event.data.type === "AR_EVENT") {
                if(event.data.quest_id === currentQuestData.id && event.data.status === 'success') {
                    console.log('resolved quest');

                    if(!currentQuestData.ar_message) return;

                    playAnswerOkAudio();
                    
                    const nextMessage = {
                        id: 'answer-ok',
                        next_message_id: currentQuestData.ar_message.next_message_id,
                        quest_id: currentQuestData.id,
                        team_token: teamToken,
                        points: currentQuestData.points
                    }

                    socket.emit("message", nextMessage);

                }
            }

        }

        if(!teamToken) return;

        socket.emit("joinTeamRoom", { teamToken });

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);

    }, [teamToken]);

    useEffect(() => {
        if (!teamToken) return;

        const handleMessageRead = async (data) => {
            console.log('Ktoś przeczytał wiadomość:', data);

            try {
                const response = await getTeamData(teamToken);
                console.log('Dane drużyny po odczytaniu wiadomości:', response);
                setChatMessagesRead(true);
            } catch (error) {
                console.error('Błąd pobierania danych drużyny', error);
                return false; 
            }
        };

        socket.on("messageReadResponse", handleMessageRead);

        return () => {
            socket.off("messageReadResponse", handleMessageRead);
        };

    }, [teamToken]);

    useEffect(() => {
        if (teamData) {
            const unlockQuest = teamData.unlockQuests[teamData.unlockQuests.length-1]; //bierzemy ostatnią odblokowaną zagadkę

            console.log('Ostatnia odblokowana zagadka:', unlockQuest);

            if(!unlockQuest) {
                setCurrentQuest(null);
                return;
            }

            // Wykryj zakończenie gry
            if (unlockQuest === 'final-quest') {
                setGameFinished(true);
                return;
            }

            fetch(`${API_URL}/api/quests/${unlockQuest}`)
                .then(response => response.json())
                .then(data => {
                    if(data && !data.error) {
                        setCurrentQuest(data);
                    }
                })
                .catch(error => console.error('Błąd podczas pobierania zagadek:', error));
        }
    }, [teamData]);

    useEffect(() => {
        if (!teamToken) return;

        const handleTeamDataUpdated = async () => {
            console.log('teamDataUpdated received, refreshing team data');
            await getTeamData(teamToken);
        };

        socket.on('teamDataUpdated', handleTeamDataUpdated);

        return () => {
            socket.off('teamDataUpdated', handleTeamDataUpdated);
        };
    }, [teamToken, socket]);

    useEffect(() => {
        const initializeToken = async () => {
            // Sprawdź, czy w URL jest token drużyny
            const params = new URLSearchParams(window.location.search);
            const tokenFromUrl = params.get('token');

            if (tokenFromUrl) {
                // Sprawdź czy token istnieje w bazie
                const isValidToken = await validateToken(tokenFromUrl);
                if (isValidToken) {
                    setTeamToken(tokenFromUrl);
                    localStorage.setItem('teamToken', tokenFromUrl);
                    console.log('Token z URL jest prawidłowy:', tokenFromUrl);
                } else {
                    console.error('Token z URL jest nieprawidłowy:', tokenFromUrl);
                    navigate("/");
                }
                return;
            } else {
                console.error('Brak utworzonego tokenu dla drużyny');
            }
        };

        initializeToken();

        correctAnswerAudioRef.current = new Audio('/assets/sounds/soundFX_goodAnswer.mp3');
        takenHintAudioRef.current = new Audio('/assets/sounds/soundFX_TakenHint.mp3');
        wrongAnswerAudioRef.current = new Audio('/assets/sounds/soundFX_wrongAnswer.mp3');

        correctAnswerAudioRef.current.preload = 'auto';
        takenHintAudioRef.current.preload = 'auto';
        wrongAnswerAudioRef.current.preload = 'auto';
        correctAnswerAudioRef.current.volume = 1;

        return () => {
            [correctAnswerAudioRef, takenHintAudioRef, wrongAnswerAudioRef].forEach(ref => {
                if (ref.current) { ref.current.pause(); ref.current = null; }
            });
        };
    }, []);

    // Funkcja sprawdzająca czy token istnieje w bazie
    async function validateToken(token) {
        if (!token) return false;
        
        try {
            const response = await getTeamData(token);
            
            if (response) {
                return true; // Token istnieje w bazie
            } else {
                return false; // Token nie istnieje lub błąd
            }
        } catch (error) {
            console.error('Błąd walidacji tokenu:', error);
            return false; // W przypadku błędu połączenia
        }
    }

    // Funkcja pobierająca wszystkie dane o drużynie
    async function getTeamData(token) {
        if (!token) return null;
        try {
            const res = await fetch(`${API_URL}/api/team?teamToken=${token}`);
            const data = await res.json();
            if (!data.error) {
                console.log('setTeamData', data);
                setTeamData(data);
                return data;
            }
            return null;
        } catch (err) {
            console.error('Błąd pobierania danych drużyny:', err);
            return null;
        }
    }

    async function addTeamPoints(points) {
        if (!teamToken || typeof points !== 'number') return;
        try {
            const res = await fetch(`${API_URL}/api/team/add-points`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ teamToken, points })
            });
            const data = await res.json();
            if (data.success) {
                await getTeamData(teamToken);
            }
        } catch (err) {
            console.error('Błąd dodawania punktów:', err);
        }
    }

    // Pobierz teamToken z localStorage
    function getTeamTokenFromLocalStorage() {
        return localStorage.getItem('teamToken') || '';
    }

    return (
        <AppStateContext.Provider value={{ 
            currentQuest,
            teamToken, 
            setTeamToken, 
            teamData,
            getTeamData,
            chatMessagesRead,
            setChatMessagesRead,
            //phoneNumber,
            //setPhoneNumber,
            setChatNotification, 
            chatNotification,
            questNotification,
            setQuestNotification,
            addTeamPoints,
            API_URL,
            playAnswerOkAudio,
            playTakenHintAudio,
            playWrongAnswerAudio,
            gameFinished,
        }}>
            {children}
        </AppStateContext.Provider>
    );
};
